import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { USER_TYPES } from '../../constants/userTypes'

const GoogleCallback = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [isProcessing, setIsProcessing] = useState(true)
  const [error, setError] = useState(null)
  const { t, i18n } = useTranslation()

  useEffect(() => {
    const handleGoogleCallback = async () => {
      try {
        // Get URL parameters
        const urlParams = new URLSearchParams(location.search)
        const code = urlParams.get('code')
        const error = urlParams.get('error')
        const state = urlParams.get('state')
        
        if (error) {
          setError(t('googleCallback.errors.generic'))
          setIsProcessing(false)
          return
        }

        if (!code) {
          setError(t('googleCallback.errors.missingCode'))
          setIsProcessing(false)
          return
        }

        // Exchange code for tokens and user data
        const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'
        
        const response = await fetch(`${API_BASE_URL}/api/v1/auth/google/callback`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            code,
            state,
            redirect_uri: 'http://127.0.0.1:8000/api/v1/auth/google/callback'
          }),
        })

        const data = await response.json()

        if (response.ok) {
          // Check if this is opened in a popup
          const isPopup = window.opener && window.opener !== window
          
          if (isPopup) {
            // Send message to parent window
            if (data.user) {
              // User exists - send success message
              window.opener.postMessage({
                type: 'GOOGLE_OAUTH_SUCCESS',
                user: data.user,
                access_token: data.access_token,
                refresh_token: data.refresh_token
              }, window.location.origin)
            } else {
              // New user - send success message with registration data
              window.opener.postMessage({
                type: 'GOOGLE_OAUTH_SUCCESS',
                user: data.google_user_data,
                access_token: data.access_token,
                refresh_token: data.refresh_token,
                isNewUser: true
              }, window.location.origin)
            }
            // Close the popup
            window.close()
          } else {
            // Handle successful Google login (normal flow)
            if (data.user) {
              // User exists - store tokens and redirect to dashboard
              localStorage.setItem('access_token', data.access_token)
              localStorage.setItem('refresh_token', data.refresh_token)
              localStorage.setItem('user_data', JSON.stringify(data.user))
              
              // User logged in successfully
              setTimeout(() => {
                // Use a custom event to trigger token sending
                window.dispatchEvent(new CustomEvent('user-logged-in'));
              }, 1000);
              
              // Check user type and redirect accordingly
              const userType = data.user.user_type || data.user.userType || data.user.type
              let redirectRoute = '/social'
              
              // ADMIN users always go to admin dashboard
              if (userType === 'ADMIN' || userType === USER_TYPES.ADMIN) {
                redirectRoute = '/admin/dashboard'
              } else if (userType === 'CLINIC_MODERATOR' || userType === USER_TYPES.CLINIC_MODERATOR) {
                redirectRoute = '/medical/clinic/redirect-to-clinic'
              } else {
                redirectRoute = localStorage.getItem('google_login_intended_route') || '/social'
              }
              
              localStorage.removeItem('google_login_intended_route')
              
              navigate(redirectRoute, {
                state: {
                  loginSuccess: true,
                  user: data.user,
                  loginMethod: 'google'
                }
              })
            } else {
              // New user - redirect to Google registration
              const userType = localStorage.getItem('google_login_user_type') || 'pet_guardian'
              localStorage.removeItem('google_login_user_type')
              
              navigate(`/auth/complete-profile/${userType}`, {
                state: {
                  googleData: data.google_user_data,
                  access_token: data.access_token
                }
              })
            }
          }
        } else {
          // Handle API errors
          const errorMessage = data.message || t('googleCallback.errors.generic')
          
          // Check if this is opened in a popup
          const isPopup = window.opener && window.opener !== window
          
          if (isPopup) {
            // Send error message to parent window
            window.opener.postMessage({
              type: 'GOOGLE_OAUTH_ERROR',
              error: errorMessage
            }, window.location.origin)
            // Close the popup
            window.close()
          } else {
            setError(errorMessage)
            setIsProcessing(false)
          }
        }
      } catch (error) {
        const errorMessage = t('googleCallback.errors.generic')
        
        // Check if this is opened in a popup
        const isPopup = window.opener && window.opener !== window
        
        if (isPopup) {
          // Send error message to parent window
          window.opener.postMessage({
            type: 'GOOGLE_OAUTH_ERROR',
            error: errorMessage
          }, window.location.origin)
          // Close the popup
          window.close()
        } else {
          setError(errorMessage)
          setIsProcessing(false)
        }
      }
    }

    handleGoogleCallback()
  }, [location, navigate, t])

  if (error) {
    return (
      <div style={{
        width: '100vw',
        height: '100vh',
        background: '#FAFAFA',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: '"Red Hat Display", sans-serif'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px',
          textAlign: 'center',
          padding: '20px'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: '#EF4444',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h2 style={{
            fontFamily: '"Red Hat Display", sans-serif',
            fontWeight: 600,
            fontSize: '24px',
            color: '#1F2937',
            margin: 0
          }}>
            {t('googleCallback.errorView.title')}
          </h2>
          <p style={{
            fontFamily: '"Red Hat Display", sans-serif',
            fontSize: '16px',
            color: '#6B7280',
            margin: 0,
            maxWidth: '400px'
          }}>
            {error}
          </p>
          <button
            onClick={() => navigate('/auth/select-type')}
            style={{
              padding: '12px 24px',
              background: '#8548A8',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontFamily: '"Red Hat Display", sans-serif',
              fontWeight: 500,
              fontSize: '16px',
              cursor: 'pointer',
              marginTop: '16px'
            }}
            dir={i18n.dir()}
          >
            {t('googleCallback.errorView.retry')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={{
      width: '100vw',
      height: '100vh',
      background: '#FAFAFA',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: '"Red Hat Display", sans-serif'
    }}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '16px'
      }} dir={i18n.dir()}>
        <Loader2 size={48} className="animate-spin" style={{ color: '#8548A8' }} />
        <p style={{
          fontFamily: '"Red Hat Display", sans-serif',
          fontSize: '18px',
          color: '#6B7280',
          margin: 0
        }}>
          {t('googleCallback.loadingMessage')}
        </p>
      </div>
    </div>
  )
}

export default GoogleCallback
